import { useContext } from 'react';
import { DataAccessContext, useAPI } from '@axo/shared/data-access/provider';
import { useQuery, useQueryClient } from 'react-query';
import { debt_register_api } from '@axo/shared/data-access/api';
import type { IAPI } from '@axo/shared/data-access/api';
import type { debt_registry } from '@axo/shared/data-access/types';
import { debtRegistryKeys } from '../../cache-keys/debtRegistryKeys.tsx';

const isNotFoundError = (error: unknown) =>
  error instanceof Error && error.message.includes(': 404:');

// Marks a failed /update fallback so the query does not retry GET + POST again.
class DebtRegistryUpdateError extends Error {}

/**
 * GET /debts 404s ("debt not found") until the register has been pulled once
 * for this application. Fall back to POST /update, which fetches + stores it.
 */
function fetchOrUpdateDebts(
  api: IAPI,
  params: { applicationID: string; SSN: string },
  onUpdated: () => Promise<void>
): Promise<debt_registry.DebtRegistryDebts> {
  return debt_register_api
    .getDebts(api)(params)
    .catch(async (error) => {
      if (!isNotFoundError(error)) throw error;
      const debts = await debt_register_api
        .updateDebts(api)(params)
        .catch((updateError: unknown) => {
          throw new DebtRegistryUpdateError(
            updateError instanceof Error ? updateError.message : 'update failed'
          );
        });
      await onUpdated();
      return debts;
    });
}

export function useDebtRegistryDebts(
  {
    applicationID,
    SSN,
  }: {
    applicationID: string;
    SSN?: string;
  },
  options?: { enabled?: boolean }
) {
  const { state } = useContext(DataAccessContext);
  const api = useAPI();
  const queryClient = useQueryClient();
  const queryKey = debtRegistryKeys.debts({
    applicationID,
    SSN,
  });
  const { enabled = true } = options ?? {};

  return useQuery({
    queryKey,
    queryFn: () =>
      fetchOrUpdateDebts(api, { applicationID, SSN: SSN ?? '' }, () =>
        // Keep parity with useUpdateDebtRegistryData: /update also refreshes /data.
        queryClient.invalidateQueries(debtRegistryKeys.data({ applicationID }))
      ),
    enabled:
      enabled &&
      state.user.token.length > 0 &&
      Boolean(applicationID) &&
      Boolean(SSN),
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof DebtRegistryUpdateError),
    // Disable refetching on focus to preserve all checked debt items.
    // A better solution will be to store the checked items in the context.
    // This should be chnaged when migrating the calculator to the applicatoin edit view.
    refetchOnWindowFocus: false,
  });
}
